import { z } from "zod";

/**
 * THE contract for exporting an AI summary — one schema, both sides of the wire.
 *
 * Three shapes used to describe the same summary: `SummaryExportSubject` in the
 * client hook, an inline zod object in the export route, and
 * `SendToSlackModalData`'s loose `summaryMarkdown?`/`summaryTitle?` optionals. The
 * client could therefore build a payload the route rejects (or, worse, one it
 * accepts and renders wrong) and nothing would catch it until a user pressed the
 * button. `SummarySubject` below is now the single definition, and the route
 * validates with the same schema the client validates against.
 *
 * Zod (not just a TS interface) because the boundary is real: the route parses an
 * untrusted request body, and the client parses an untrusted response. Types
 * alone check neither.
 *
 * Pure — no server-only imports — so both sides import it directly.
 */

/** Ceiling on a summary body. Matches the TTS input cap; past this it isn't a summary. */
export const MAX_SUMMARY_CHARS = 100_000;
/** Slack canvas titles cap at 250; keep every surface's title within that. */
export const MAX_SUMMARY_TITLE_CHARS = 250;

/**
 * Only http(s) may be embedded as a link in an exported document.
 *
 * `z.string().url()` alone accepts `javascript:alert(1)` — and this value is
 * written into a .md/.txt/PDF that gets forwarded and opened elsewhere.
 */
export const httpUrlSchema = z
  .string()
  .url()
  .max(2000)
  .refine((value) => /^https?:\/\//i.test(value), {
    message: "must be an http(s) URL",
  });

/**
 * The summary itself: what it's called, what it says, what it's about.
 *
 * Shared by the action bar's props, the export request, and the Slack canvas
 * payload, so those three cannot drift apart again.
 */
export const summarySubjectSchema = z.object({
  /** Panel title — "Company briefing", "Ticket briefing". */
  title: z.string().trim().min(1).max(MAX_SUMMARY_TITLE_CHARS),
  /** The summary body, as markdown. */
  markdown: z.string().min(1).max(MAX_SUMMARY_CHARS),
  /** Record name: under the PDF title, and in the download filename. */
  subject: z.string().trim().max(250).optional(),
  /** Absolute Hub URL of the record this summarizes. */
  sourceUrl: httpUrlSchema.optional(),
});

export type SummarySubject = z.infer<typeof summarySubjectSchema>;

/** File formats the export route produces. Audio is NOT here — see below. */
export const summaryFileFormatSchema = z.enum(["markdown", "text", "pdf"]);
export type SummaryFileFormat = z.infer<typeof summaryFileFormatSchema>;

/** Clipboard formats. */
export const summaryCopyFormatSchema = z.enum(["text", "markdown"]);
export type SummaryCopyFormat = z.infer<typeof summaryCopyFormatSchema>;

/**
 * The export request body.
 *
 * `audio` is deliberately NOT a member: audio comes from `POST /api/ai/tts`,
 * which owns Polly synthesis and its own caching. Keeping the two apart at the
 * TYPE level is what makes "download the summary" and "download the audio"
 * impossible to confuse again — a bare Download button that produced an MP3 was
 * the original bug.
 */
export const summaryExportRequestSchema = summarySubjectSchema.extend({
  format: summaryFileFormatSchema,
});

export type SummaryExportRequest = z.infer<typeof summaryExportRequestSchema>;

/**
 * Error codes the export route returns. A code (not just prose) so the client can
 * decide whether to offer a retry, a different format, or nothing — per
 * ERROR_STATES_ACTIONABLE, the UI has to offer the fix, which means it has to
 * know which fix applies.
 */
export const SUMMARY_EXPORT_ERROR_CODES = [
  /** Body failed schema validation — a bug in the caller, not the user's doing. */
  "SUMMARY_EXPORT_INVALID_REQUEST",
  /** Over MAX_SUMMARY_CHARS. Copying still works, so say so. */
  "SUMMARY_EXPORT_TOO_LARGE",
  /** Renderer threw (PDF layout, encoding). Another format will usually work. */
  "SUMMARY_EXPORT_RENDER_FAILED",
  /** Signed out / session expired mid-session. */
  "SUMMARY_EXPORT_UNAUTHORIZED",
] as const;

export type SummaryExportErrorCode = (typeof SUMMARY_EXPORT_ERROR_CODES)[number];

export const summaryExportErrorSchema = z.object({
  error: z.string().min(1),
  code: z.enum(SUMMARY_EXPORT_ERROR_CODES),
  /** Which format was attempted, when the failure is format-specific. */
  format: summaryFileFormatSchema.optional(),
});

export type SummaryExportError = z.infer<typeof summaryExportErrorSchema>;

/**
 * User-facing text per code, each naming what to do next rather than only what
 * went wrong.
 */
export const SUMMARY_EXPORT_ERROR_MESSAGES: Record<SummaryExportErrorCode, string> = {
  SUMMARY_EXPORT_INVALID_REQUEST:
    "This summary can't be exported yet — try regenerating it first.",
  SUMMARY_EXPORT_TOO_LARGE:
    "This summary is too long to export as a file. Copy it instead, or regenerate a shorter one.",
  SUMMARY_EXPORT_RENDER_FAILED:
    "Couldn't build that file. Try a different format — Markdown and plain text are the most reliable.",
  SUMMARY_EXPORT_UNAUTHORIZED: "Your session expired. Reload the page and try again.",
};

/**
 * Turn an unknown error response into a code + message.
 *
 * Never throws and never surfaces a raw server string: an unrecognized body maps
 * to RENDER_FAILED, whose message ("try a different format") is the right advice
 * for an unknown server-side failure anyway.
 */
export function parseSummaryExportError(
  status: number,
  body: unknown
): { code: SummaryExportErrorCode; message: string } {
  const parsed = summaryExportErrorSchema.safeParse(body);
  if (parsed.success) {
    return {
      code: parsed.data.code,
      message: SUMMARY_EXPORT_ERROR_MESSAGES[parsed.data.code],
    };
  }
  const code: SummaryExportErrorCode =
    status === 401 || status === 403
      ? "SUMMARY_EXPORT_UNAUTHORIZED"
      : status === 413
        ? "SUMMARY_EXPORT_TOO_LARGE"
        : status === 400
          ? "SUMMARY_EXPORT_INVALID_REQUEST"
          : "SUMMARY_EXPORT_RENDER_FAILED";
  return { code, message: SUMMARY_EXPORT_ERROR_MESSAGES[code] };
}

/**
 * Validate a subject before spending a round trip on it.
 *
 * Returns the parsed subject or a code + message — so a summary that is still
 * streaming, or one that blew the size cap, is refused in the client with the
 * same rules the server applies, rather than producing a 400 the user has to
 * interpret.
 */
export function validateSummarySubject(
  input: unknown
): { ok: true; subject: SummarySubject } | { ok: false; code: SummaryExportErrorCode; message: string } {
  const parsed = summarySubjectSchema.safeParse(input);
  if (parsed.success) return { ok: true, subject: parsed.data };

  const tooLong = parsed.error.issues.some(
    (issue) => issue.code === "too_big" && issue.path.includes("markdown")
  );
  const code: SummaryExportErrorCode = tooLong
    ? "SUMMARY_EXPORT_TOO_LARGE"
    : "SUMMARY_EXPORT_INVALID_REQUEST";
  return { ok: false, code, message: SUMMARY_EXPORT_ERROR_MESSAGES[code] };
}
